import React, { useState, useEffect } from 'react';
import { PWAUtils } from '../utils/pwaUtils';

const PWADebug = () => {
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    // Solo in modalità sviluppo
    if (process.env.NODE_ENV === 'development') {
      const info = PWAUtils.getDebugInfo();
      setDebugInfo(info);
    }
  }, []);

  const resetPWAPrompts = () => {
    // Reset completo dei pannelli PWA
    localStorage.removeItem('installPromptDismissed');
    localStorage.removeItem('installPromptCount');
    localStorage.removeItem('installPromptNextShow');
    localStorage.removeItem('iosInstallPromptDismissed');
    localStorage.removeItem('iosInstallPromptCount');
    localStorage.removeItem('iosInstallPromptNextShow');
    
    alert('Reset completato! Ricarica la pagina per testare i pannelli.');
  };

  const forceShowPrompt = () => {
    // Forza la visualizzazione del prompt creando un evento artificiale
    const event = new Event('beforeinstallprompt');
    event.preventDefault = () => {};
    event.prompt = () => Promise.resolve();
    event.userChoice = Promise.resolve({ outcome: 'dismissed' });
    
    // Simula un deferredPrompt valido
    Object.defineProperty(event, 'preventDefault', {
      value: () => {},
      writable: false
    });
    
    console.log('Forcing install prompt event...');
    window.dispatchEvent(event);
  };

  const forceShowPanelDirect = () => {
    // Forza la visualizzazione diretta del pannello senza evento
    // Rimuove temporaneamente le restrizioni
    localStorage.removeItem('installPromptDismissed');
    localStorage.removeItem('installPromptCount');
    localStorage.removeItem('installPromptNextShow');
    
    // Crea un evento forzato più realistico
    const mockEvent = {
      preventDefault: () => {},
      prompt: () => {
        alert('Questo è un test! In una PWA reale, qui si aprirebbe il dialog di installazione del browser.');
        return Promise.resolve();
      },
      userChoice: Promise.resolve({ outcome: 'dismissed' })
    };
    
    // Simula l'evento beforeinstallprompt
    const event = new CustomEvent('beforeinstallprompt', { detail: mockEvent });
    Object.assign(event, mockEvent);
    
    window.dispatchEvent(event);
  };

  const refreshDebugInfo = () => {
    if (process.env.NODE_ENV === 'development') {
      const info = PWAUtils.getDebugInfo();
      setDebugInfo(info);
    }
  };

  const testWebShareAPI = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Test Web Share API',
          text: 'Test per aprire il menu share nativo di iOS Safari',
          url: window.location.href
        });
        alert('Web Share API funziona! Il menu share è stato aperto.');
      } catch (error) {
        alert(`Web Share API error: ${error.message}`);
      }
    } else {
      alert('Web Share API non supportata in questo browser.');
    }
  };

  // Solo in modalità sviluppo
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <>
      {/* Bottone floating per aprire il debug */}
      <button
        className="btn btn-danger position-fixed"
        style={{ 
          bottom: '20px', 
          right: '20px', 
          zIndex: 1060,
          borderRadius: '50%',
          width: '60px',
          height: '60px'
        }}
        onClick={() => setShowDebug(!showDebug)}
        title="PWA Debug"
      >
        🔧
      </button>

      {/* Pannello di debug */}
      {showDebug && (
        <div 
          className="position-fixed bottom-0 start-0 end-0 bg-dark text-white p-3"
          style={{ zIndex: 1055, maxHeight: '70vh', overflowY: 'auto' }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">🔧 PWA Debug Info</h6>
            <button 
              className="btn btn-sm btn-outline-light"
              onClick={() => setShowDebug(false)}
            >
              ✕
            </button>
          </div>

          {debugInfo && (
            <div className="row">
              <div className="col-md-6">
                <strong>Stato PWA:</strong>
                <ul className="mb-3">
                  <li>Installata: {debugInfo.isInstalled ? '✅' : '❌'}</li>
                  <li>iOS: {debugInfo.isIOS ? '✅' : '❌'}</li>
                  <li>Android: {debugInfo.isAndroid ? '✅' : '❌'}</li>
                  <li>PWA Supportata: {debugInfo.isPWASupported ? '✅' : '❌'}</li>
                  <li>Service Worker: {debugInfo.serviceWorkerStatus}</li>
                  <li>Display Mode: {debugInfo.displayMode}</li>
                  <li>Protocol: {debugInfo.protocol}</li>
                  <li>Secure Context: {debugInfo.isSecureContext ? '✅' : '❌'}</li>
                  <li>Web Share API: {navigator.share ? '✅' : '❌'}</li>
                </ul>
              </div>
              
              <div className="col-md-6">
                <strong>Installabilità:</strong>
                <ul className="mb-3">
                  <li>Installabile: {debugInfo.installability.isInstallable ? '✅' : '❌'}</li>
                  {debugInfo.installability.missing.length > 0 && (
                    <li className="text-warning">
                      Mancante: {debugInfo.installability.missing.join(', ')}
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}

          <div className="mb-3">
            <strong>LocalStorage PWA:</strong>
            <ul>
              <li>Install Dismissed: {localStorage.getItem('installPromptDismissed') || 'Non impostato'}</li>
              <li>Install Count: {localStorage.getItem('installPromptCount') || '0'}</li>
              <li>iOS Dismissed: {localStorage.getItem('iosInstallPromptDismissed') || 'Non impostato'}</li>
              <li>iOS Count: {localStorage.getItem('iosInstallPromptCount') || '0'}</li>
            </ul>
          </div>

          <div className="d-flex flex-wrap gap-2 mb-2">
            <button 
              className="btn btn-sm btn-warning"
              onClick={resetPWAPrompts}
            >
              Reset Pannelli PWA
            </button>
            <button 
              className="btn btn-sm btn-info"
              onClick={forceShowPrompt}
            >
              Forza Prompt (Event)
            </button>
            <button 
              className="btn btn-sm btn-primary"
              onClick={forceShowPanelDirect}
            >
              Forza Pannello (Direct)
            </button>
            <button 
              className="btn btn-sm btn-success"
              onClick={testWebShareAPI}
            >
              Test Web Share API
            </button>
          </div>

          <div className="d-flex flex-wrap gap-2 mb-3">
            <button 
              className="btn btn-sm btn-success"
              onClick={() => window.location.reload()}
            >
              Ricarica Pagina
            </button>
            <button 
              className="btn btn-sm btn-secondary"
              onClick={refreshDebugInfo}
            >
              Aggiorna Info
            </button>
          </div>

          <div className="mt-3">
            <small className="text-muted">
              User Agent: {navigator.userAgent.substring(0, 100)}...
            </small>
          </div>
        </div>
      )}
    </>
  );
};

export default PWADebug; 